import { cloneDeep, compact } from 'lodash/fp';
import { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import AccountStatusSticker from 'dashboard/components/AccountStatusSticker';
import ClickableDetail from 'dashboard/components/ClickableDetail';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { CaseObject } from 'dashboard/models/case';
import { Inquiry } from 'dashboard/models/inquiry';
import { CaseModuleBaseProps } from 'dashboard/modules/CaseModuleBase/CaseModuleBase';
import { ModuleLayoutByAccount } from 'dashboard/modules/LayoutByAccount/model';
import Placeholder from 'dashboard/modules/Placeholder';
import { ResourceModules } from 'dashboard/modules/Resource';
import { CaseApi } from 'dashboard/views/DashboardCaseShowView/DashboardCaseShowView';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';
import { ApiModel } from 'lib/interfaces/api';

const ChildModuleContainer = styled('div')`
  border-radius: ${({ theme }) => theme.borderRadius};
  /* using outline here rather than border, allows us to maintain margin collapsing (sorta hacky tho) */
  outline: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const AccountNameContainer = styled.div`
  display: flex;
  align-items: center;
`;

const LinkRow = styled('div')`
  grid-column-end: span 3;
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
  font-weight: ${FontWeight.Regular};
`;

export interface LayoutByAccountProps {
  accounts: Account[];
  accountStatuses: AccountStatus[];
  module: ModuleLayoutByAccount;
  dataApi: CaseApi;
  baseComponent: ComponentType<CaseModuleBaseProps<ResourceModules>>;
  runCodeWithContext: (code: string) => Promise<unknown>;
}

function LayoutByAccount({
  module,
  accounts,
  accountStatuses,
  dataApi,
  baseComponent: Base,
  runCodeWithContext,
}: LayoutByAccountProps) {
  const ignoredTags = module['ignored-tags'] ?? [];
  const filteredAccounts = accounts.filter((obj) => {
    const tagIds = obj.relationships.tags?.data.map((tag) => tag.id) ?? [];
    const hasTag = ignoredTags.filter((ignoredTag) => tagIds.includes(ignoredTag)).length !== 0;

    const isAccountType = module['account-type']
      ? obj.relationships['account-type']?.data.id === module['account-type']
      : true;

    return isAccountType && !hasTag;
  });

  if (filteredAccounts.length === 0) {
    return <Placeholder title='Account objects' message='No data collected for this case' />;
  }

  return (
    <div data-test='module__layout-by-account'>
      {filteredAccounts.map((account) => {
        const filteredDataApi = {
          ...cloneDeep(dataApi),
          caseObjects: filterCaseObjectsByAccount(account, dataApi.caseObjects),
        };

        const accountStatus = accountStatuses.find(
          (obj) => obj.id === account.relationships['account-status']?.data.id,
        );

        let title: ReactNode;
        let fallbackTitle: ReactNode;
        if (module['group-title-field-key']) {
          title = String(account.attributes.fields[module['group-title-field-key']]?.value ?? '');
          fallbackTitle = `No value collected for ${module['group-title-field-key']}`;
        } else {
          title = account.attributes['name-full-localized'] ?? account.attributes['name-full'];
          fallbackTitle = 'No name collected';
        }

        return (
          <ClickableDetail
            key={account.id}
            header={
              <AccountNameContainer>
                {title ? (
                  <Heading size='sm'>{title}</Heading>
                ) : (
                  <Heading size='sm' color='secondary'>
                    {fallbackTitle}
                  </Heading>
                )}
                {module['show-status-in-title'] && accountStatus && (
                  <>
                    <Spacer width={1} />
                    <AccountStatusSticker accountStatus={accountStatus} />
                  </>
                )}
              </AccountNameContainer>
            }
            subheader={
              <LinkRow>
                <StyledLink to={`/dashboard/accounts/${account.id}`}>{account.id}</StyledLink>
              </LinkRow>
            }
            isDefaultOpen={true}
          >
            {compact(
              module.resources.map((resource) => {
                if (resource.type !== ModuleTypes.Resource) {
                  return;
                }

                return (
                  <ChildModuleContainer key={`${account.id}-${resource.token}`}>
                    <Base
                      module={resource}
                      caseApi={filteredDataApi}
                      runCodeWithContext={runCodeWithContext}
                    />
                  </ChildModuleContainer>
                );
              }),
            )}
          </ClickableDetail>
        );
      })}
    </div>
  );
}

const filterCaseObjectsByAccount = (account: Account, caseObjects: CaseObject[]): CaseObject[] => {
  let validObjectIds: string[] = [];

  validObjectIds = caseObjects
    .filter((obj) => {
      if ('account' in obj.relationships) {
        return obj.relationships.account?.data?.id === account.id;
      } else {
        return false;
      }
    })
    .map((obj) => obj.id);

  const inquiries: Inquiry[] = caseObjects.filter((obj) => {
    // Not every inquiry will have an account
    return obj.type === ApiModel.Inquiry && obj.relationships.account.data?.id === account.id;
  }) as Inquiry[];

  validObjectIds = [
    ...validObjectIds,
    ...inquiries.flatMap((obj) =>
      (obj.relationships.verifications?.data ?? []).map((datum) => datum.id),
    ),
  ];

  validObjectIds = [
    ...validObjectIds,
    ...(account.relationships.reports?.data ?? []).map((datum) => datum.id),
  ];

  return caseObjects.filter((obj) => validObjectIds.includes(obj.id));
};

export default LayoutByAccount;
